/**
 * The ONE decoded-AudioBuffer cache for the edit engines.
 *
 * WHY A CACHE
 * -----------
 * Editing is a burst activity — a user fixing connected speech does three or
 * four operations in a row on the same recording — and decoding a 45 s m4a each
 * time is a visible stall on a phone.
 *
 * WHY EXACTLY ONE SLOT
 * --------------------
 * Two tasks' worth of decoded PCM is tens of megabytes and this is a phone. A
 * second task simply evicts the first.
 *
 * WHY ONE MODULE
 * --------------
 * Before the restructure this cache existed THREE times: byte-identical copies
 * in `segmentEdits.ts` and `cutOps.ts` (so a merge and a drag on the same task
 * each decoded it separately, and a cut through one left the other's copy
 * stale), plus a third in `components/taskAudio.ts`. That third one STAYS where
 * it is: it is ref-counted against lane open/close, a different lifecycle from
 * this one, and merging them would tie a UI component's mount to an edit
 * engine's working set. See its header.
 *
 * INVALIDATION — the known gap
 * ----------------------------
 * {@link forget} is the hook, and it currently has ZERO callers: nothing drops
 * the buffer when a task's audio is replaced or the task is deleted. `db`'s
 * `deleteTask`, `putTaskAudio` and `clearAll` each carry a `TODO(E2)` naming
 * this function, but db → services is an upward import (see ARCHITECTURE.md,
 * rule R4) so they cannot call it directly. The correct fix is a change-bus
 * subscription here; that is a behavior change and is deferred as follow-up
 * F11. Until then this is a latent stale-buffer bug, not merely dead code.
 */

import { getTaskAudio } from '../db';
import { decodeAudioBlob } from './audioSlicer';

/** The last decoded task file. Exactly one entry, by design (see header). */
let cached: { taskId: string; buffer: AudioBuffer } | null = null;

/**
 * The decoded buffer for a task, decoding it if this is not the cached one.
 *
 * `missingAudioMessage` is a parameter rather than a constant because the two
 * call sites phrase the failure differently and both strings are user-facing:
 * the merge engine explains that the clip cannot be re-cut, the Cuts engine
 * just says the recording is gone. Neither wording was invented here and
 * neither is changed by the consolidation.
 */
export async function decodedAudioFor(taskId: string, missingAudioMessage: string): Promise<AudioBuffer> {
  if (cached && cached.taskId === taskId) return cached.buffer;
  const blob = await getTaskAudio(taskId);
  if (!blob) throw new Error(missingAudioMessage);
  const buffer = await decodeAudioBlob(blob);
  cached = { taskId, buffer };
  return buffer;
}

/**
 * Drop the cache — call when a task's audio is replaced or deleted.
 *
 * No argument clears whatever is held; an argument clears it only if it is that
 * task's. NOT CURRENTLY CALLED — see the header.
 */
export function forget(taskId?: string): void {
  if (!taskId || cached?.taskId === taskId) cached = null;
}
